import type { TFunction } from "i18next";
import { buildInitialOpportunitiesLinkSearch } from "@/features/opportunities/constants/opportunity-filters";
import { DEFAULT_EXPLORE_SEARCH, type ExploreSearch } from "@/features/dashboard/lib/explore-search";
import type { AskNexusAnswerBlock, AskNexusCard, AskNexusLinkCard } from "@/features/ask-nexus/api/ask-nexus.api";

/**
 * Where an Ask Nexus link goes, built from the app's own routes and their search
 * helpers. `page` is the PAGE permission the destination route itself requires;
 * the route still checks it, and the server still authorizes its data.
 */
export type NexusLinkRoute =
  | { to: "/opportunities"; search: ReturnType<typeof buildInitialOpportunitiesLinkSearch>; page: "opportunities" }
  | { to: "/persona-opportunities"; search: { setId?: string; hubTab?: "scripts" }; page: "persona-opportunities" }
  | { to: "/personas"; search: { persona: undefined }; page: "personas" }
  | { to: "/dashboard"; search: ExploreSearch; page: "dashboard" }
  | { to: "/overview"; search: ExploreSearch; page: "overview" };

/** Opportunity Hub holds persona sets; the server reads a row's kind the same way. */
const PERSONA_OPPORTUNITY_TYPE = "PERSONA";

/** Suggestion cards hold content Ask Nexus wrote, not saved anywhere: each points to where the person can continue. */
const SUGGESTION_DESTINATIONS: Partial<Record<AskNexusCard["card"], AskNexusLinkCard>> = {
  script: { card: "link", destination: "scripts" },
  opportunity_suggestion: { card: "link", destination: "opportunity_hub" },
};

/**
 * A persona opportunity opens its set in Opportunity Hub, which cannot select one
 * opportunity; any other opens in its own drawer on the Opportunities page.
 */
export function nexusLinkRoute(link: AskNexusLinkCard): NexusLinkRoute {
  switch (link.destination) {
    case "personas":
      return { to: "/personas", search: { persona: undefined }, page: "personas" };
    case "opportunity_hub":
      return { to: "/persona-opportunities", search: {}, page: "persona-opportunities" };
    case "scripts":
      // The hub's Scripts tab across every set, where people import or write scripts themselves.
      return { to: "/persona-opportunities", search: { hubTab: "scripts" }, page: "persona-opportunities" };
    // The defaults, as the header menu opens them: any other value makes the page replace the person's own filters.
    case "dashboard":
      return { to: "/dashboard", search: DEFAULT_EXPLORE_SEARCH, page: "dashboard" };
    case "overview":
      return { to: "/overview", search: DEFAULT_EXPLORE_SEARCH, page: "overview" };
    case "opportunity":
      if (link.setKind === "persona") return { to: "/persona-opportunities", search: { setId: link.setId }, page: "persona-opportunities" };
      return {
        to: "/opportunities",
        search: buildInitialOpportunitiesLinkSearch({ tab: "active", setId: link.setId, opportunityId: link.opportunityId }),
        page: "opportunities",
      };
  }
}

/** The link of a saved opportunity a card shows. */
export function opportunityLink(item: { id: string; setId: string; type: string }): AskNexusLinkCard {
  return { card: "link", destination: "opportunity", opportunityId: item.id, setId: item.setId, setKind: item.type === PERSONA_OPPORTUNITY_TYPE ? "persona" : "legacy" };
}

/** Says where the link lands, so a set link never promises the single opportunity, and no link claims to save anything. */
export function nexusLinkLabel(t: TFunction, link: AskNexusLinkCard): string {
  switch (link.destination) {
    case "personas":
      return t("askNexus.links.personas", "View personas");
    case "opportunity_hub":
      return t("askNexus.links.opportunityHub", "View Opportunity Hub");
    case "scripts":
      return t("askNexus.links.scripts", "Open scripts");
    case "dashboard":
      return t("askNexus.links.dashboard", "Open Dashboard");
    case "overview":
      return t("askNexus.links.overview", "Open Overview");
    case "opportunity":
      return link.setKind === "persona" ? t("askNexus.links.opportunityInHub", "View in Opportunity Hub") : t("askNexus.links.opportunity", "View opportunity");
  }
}

/** Buttons in the row after an answer; a saved opportunity's own link is in its card and not counted. */
export const MAX_NEXT_STEPS = 2;

/**
 * The next steps after an answer: each destination once, only those the person
 * can open, at most `MAX_NEXT_STEPS`. Where the answer's own suggestion cards
 * continue comes first, then the pages the agent pointed to, in its order. A
 * saved-opportunity card links its own items, so the agent's link to one of
 * those is left out.
 */
export function nextStepLinks(blocks: AskNexusAnswerBlock[], canOpen: (link: AskNexusLinkCard) => boolean): AskNexusLinkCard[] {
  const shownInCards = new Set<string>();
  const fromCards: AskNexusLinkCard[] = [];
  const fromAgent: AskNexusLinkCard[] = [];
  for (const block of blocks) {
    if (block.type !== "card") continue;
    if (block.card.card === "opportunities") block.card.items.forEach((item) => shownInCards.add(linkKey(opportunityLink(item))));
    const suggested = SUGGESTION_DESTINATIONS[block.card.card];
    if (suggested) fromCards.push(suggested);
    if (block.card.card === "link") fromAgent.push(block.card);
  }
  const seen = new Set(shownInCards);
  const unique = [...fromCards, ...fromAgent].filter((link) => {
    const key = linkKey(link);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.filter(canOpen).slice(0, MAX_NEXT_STEPS);
}

/** Two links with the same key open the same page in the same state. */
function linkKey(link: AskNexusLinkCard): string {
  const route = nexusLinkRoute(link);
  return `${route.to}?${JSON.stringify(route.search)}`;
}
